import { useState } from 'react'
import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { createProjectInvitation } from '@/lib/api/projectMembers'
import type { ProjectRole } from '@/types/project-teams'
import { RoleLabels, RoleDescriptions } from '@/types/project-teams'
import { toast } from 'sonner'
import { Loader2, Shield, Pencil, Eye, Users, Copy, CheckCircle2 } from 'lucide-react'
import { useAnalytics } from '@/hooks/useAnalytics'

interface InviteMemberDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  projectId: string
  onInviteSent: () => void
}

export function InviteMemberDialog({
  open,
  onOpenChange,
  projectId,
  onInviteSent,
}: InviteMemberDialogProps) {
  const [email, setEmail] = useState('')
  const [role, setRole] = useState<ProjectRole>('editor')
  const [loading, setLoading] = useState(false)
  const [invitationUrl, setInvitationUrl] = useState<string | null>(null)
  const [emailSent, setEmailSent] = useState(false)
  const [copied, setCopied] = useState(false)
  const { trackTeamMemberInvited } = useAnalytics()

  const handleClose = () => {
    // Reset state when dialog closes
    setEmail('')
    setRole('editor')
    setInvitationUrl(null)
    setEmailSent(false)
    setCopied(false)
    onOpenChange(false)
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()

    if (!email.trim()) {
      toast.error('Email is required')
      return
    }

    // Basic email validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
    if (!emailRegex.test(email)) {
      toast.error('Please enter a valid email address')
      return
    }

    try {
      setLoading(true)

      const result = await createProjectInvitation({
        project_id: projectId,
        email: email.trim(),
        role,
      })

      // Store the invitation URL and email status
      setInvitationUrl(result.invitation_url)
      setEmailSent(result.email_sent)

      // Track team member invitation
      trackTeamMemberInvited({
        project_id: projectId,
        member_email: email.trim(),
        role,
      })

      if (result.email_sent) {
        toast.success(`Invitation email sent to ${email}`)
        // Close dialog after success
        setTimeout(() => {
          handleClose()
          onInviteSent()
        }, 1000)
      } else {
        toast.warning('Email could not be sent. Please share the invitation link manually.')
        // Still notify parent to refresh the invitations list
        onInviteSent()
      }
    } catch (error: any) {
      console.error('Error inviting member:', error)
      toast.error(error.message || 'Failed to send invitation')
    } finally {
      setLoading(false)
    }
  }

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent>
        <form onSubmit={handleSubmit}>
          <DialogHeader>
            <DialogTitle>Invite Team Member</DialogTitle>
            <DialogDescription>
              Send an invitation to collaborate on this project
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="email">Email Address</Label>
              <Input
                id="email"
                type="email"
                placeholder="colleague@example.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="role">Role</Label>
              <Select
                value={role}
                onValueChange={(value) => setRole(value as ProjectRole)}
              >
                <SelectTrigger>
                  <SelectValue>
                    <div className="flex items-center gap-2">
                      {role === 'admin' && <Shield className="h-4 w-4" />}
                      {role === 'editor' && <Pencil className="h-4 w-4" />}
                      {role === 'viewer' && <Eye className="h-4 w-4" />}
                      {role === 'contributor' && <Users className="h-4 w-4" />}
                      <span>{RoleLabels[role]}</span>
                    </div>
                  </SelectValue>
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="admin">
                    <div className="flex items-start gap-2">
                      <Shield className="h-4 w-4 mt-0.5 flex-shrink-0" />
                      <div className="flex-1 min-w-0">
                        <div className="font-medium">{RoleLabels.admin}</div>
                        <div className="text-xs text-muted-foreground">
                          {RoleDescriptions.admin}
                        </div>
                      </div>
                    </div>
                  </SelectItem>
                  <SelectItem value="editor">
                    <div className="flex items-start gap-2">
                      <Pencil className="h-4 w-4 mt-0.5 flex-shrink-0" />
                      <div className="flex-1 min-w-0">
                        <div className="font-medium">{RoleLabels.editor}</div>
                        <div className="text-xs text-muted-foreground">
                          {RoleDescriptions.editor}
                        </div>
                      </div>
                    </div>
                  </SelectItem>
                  <SelectItem value="viewer">
                    <div className="flex items-start gap-2">
                      <Eye className="h-4 w-4 mt-0.5 flex-shrink-0" />
                      <div className="flex-1 min-w-0">
                        <div className="font-medium">{RoleLabels.viewer}</div>
                        <div className="text-xs text-muted-foreground">
                          {RoleDescriptions.viewer}
                        </div>
                      </div>
                    </div>
                  </SelectItem>
                  <SelectItem value="contributor">
                    <div className="flex items-start gap-2">
                      <Users className="h-4 w-4 mt-0.5 flex-shrink-0" />
                      <div className="flex-1 min-w-0">
                        <div className="font-medium">{RoleLabels.contributor}</div>
                        <div className="text-xs text-muted-foreground">
                          {RoleDescriptions.contributor}
                        </div>
                      </div>
                    </div>
                  </SelectItem>
                </SelectContent>
              </Select>
            </div>

            {!invitationUrl && (
              <div className="rounded-lg bg-muted p-3 text-sm">
                <p className="font-medium mb-1">Invitation will expire in 7 days</p>
                <p className="text-muted-foreground text-xs">
                  The invitee will receive a secure link to join this project
                </p>
              </div>
            )}

            {invitationUrl && !emailSent && (
              <div className="rounded-lg bg-yellow-50 dark:bg-yellow-950/20 border border-yellow-200 dark:border-yellow-900 p-3 text-sm space-y-2">
                <div className="flex items-start gap-2">
                  <div className="flex-1">
                    <p className="font-medium text-yellow-900 dark:text-yellow-100 mb-1">
                      Email delivery not available
                    </p>
                    <p className="text-yellow-700 dark:text-yellow-300 text-xs mb-2">
                      Please copy and share this invitation link manually with {email}
                    </p>
                    <div className="flex items-center gap-2">
                      <Input
                        value={invitationUrl}
                        readOnly
                        className="text-xs font-mono bg-white dark:bg-gray-950"
                      />
                      <Button
                        type="button"
                        size="sm"
                        variant="secondary"
                        onClick={() => {
                          navigator.clipboard.writeText(invitationUrl)
                          setCopied(true)
                          toast.success('Invitation link copied!')
                          setTimeout(() => setCopied(false), 2000)
                        }}
                      >
                        {copied ? (
                          <>
                            <CheckCircle2 className="h-4 w-4 mr-1" />
                            Copied
                          </>
                        ) : (
                          <>
                            <Copy className="h-4 w-4 mr-1" />
                            Copy
                          </>
                        )}
                      </Button>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>

          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={handleClose}
              disabled={loading}
            >
              {invitationUrl && !emailSent ? 'Done' : 'Cancel'}
            </Button>
            {(!invitationUrl || emailSent) && (
              <Button type="submit" disabled={loading}>
                {loading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                Send Invitation
              </Button>
            )}
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  )
}
